/**
 * URL layout:
 *   /                              -> Landing
 *   /privacy                       -> Privacy notice
 *   /{projectId}                   -> content editor at root pointer
 *   /{projectId}#/pages/0          -> content editor at /pages/0
 *   /{projectId}/schema            -> schema editor at root pointer
 *   /{projectId}/schema#/properties/title -> schema editor at /properties/title
 *
 * The project id IS the URL so two projects can open simultaneously in
 * different tabs without sharing state. Within a project, the hash carries
 * the zoom pointer — see useUrlSync.ts.
 *
 * We intentionally don't use a full router here. The app has a small set
 * of top-level states (landing, privacy, content editor, schema editor)
 * and trying to layer react-router on top would fight useUrlSync's hash
 * management.
 *
 * `/privacy` and `/faq` are reserved: a project with the literal id
 * "privacy" or "faq" would collide. Project ids are uuids so this is not a
 * practical concern.
 */

export type EditorMode = 'content' | 'schema';

export const PRIVACY_PATH = '/privacy';
export const FAQ_PATH = '/faq';
export const INVITE_ACCEPT_PATH = '/invite/accept';

/**
 * Custom event fired after any of the `navigateTo*` helpers calls
 * `pushState`. Browsers don't fire `popstate` for `pushState`, so
 * top-level state machines that follow the URL need an explicit signal.
 * Listen alongside `popstate` to react to both back/forward AND
 * programmatic navigation.
 */
export const ROUTE_CHANGE_EVENT = 'bliksys:route-change';

function announceRouteChange(): void {
  window.dispatchEvent(new Event(ROUTE_CHANGE_EVENT));
}

export interface RouteState {
  projectId: string | null;
  mode: EditorMode;
  hash: string;
}

/** Read the current location into a RouteState. */
export function parseLocation(): RouteState {
  const path = window.location.pathname;
  const segments = path
    .replace(/^\/+|\/+$/g, '')
    .split('/')
    .filter(Boolean);
  const first = segments[0];
  const second = segments[1];
  return {
    projectId: first ? decodeURIComponent(first) : null,
    mode: second === 'schema' ? 'schema' : 'content',
    hash: window.location.hash,
  };
}

/** Build a URL for a project (hash optional). */
export function projectUrl(projectId: string, hash = '', mode: EditorMode = 'content'): string {
  const base = '/' + encodeURIComponent(projectId) + (mode === 'schema' ? '/schema' : '');
  return base + (hash.startsWith('#') ? hash : '');
}

/** Push a navigation to open a project (optionally at a specific hash + mode). */
export function navigateToProject(
  projectId: string,
  hash = '',
  mode: EditorMode = 'content',
): void {
  window.history.pushState({ projectId, mode }, '', projectUrl(projectId, hash, mode));
  announceRouteChange();
}

/** Push a navigation back to the landing page. */
export function navigateToLanding(): void {
  window.history.pushState({}, '', '/');
  announceRouteChange();
}

/** Push a navigation to the privacy notice. */
export function navigateToPrivacy(): void {
  window.history.pushState({}, '', PRIVACY_PATH);
  announceRouteChange();
}

/** True when the current URL is the privacy notice. */
export function isPrivacyPath(): boolean {
  return window.location.pathname.replace(/\/+$/, '') === PRIVACY_PATH;
}

/** Push a navigation to the FAQ page. */
export function navigateToFaq(): void {
  window.history.pushState({}, '', FAQ_PATH);
  announceRouteChange();
}

/** True when the current URL is the FAQ page. */
export function isFaqPath(): boolean {
  return window.location.pathname.replace(/\/+$/, '') === FAQ_PATH;
}

/** True when the current URL is the invite-accept page. An invite is
 * inherently a cloud action, so the app forces cloud mode on this path
 * (even in dev, where there's otherwise no `?cloud` flag). */
export function isInvitePath(): boolean {
  return window.location.pathname.replace(/\/+$/, '') === INVITE_ACCEPT_PATH;
}

/** The `token` query param on the invite-accept page, or null. */
export function inviteToken(): string | null {
  if (!isInvitePath()) return null;
  return new URLSearchParams(window.location.search).get('token');
}

/** The `auth_error` query param (set when the API bounces a failed
 * magic-link / OAuth flow back to the app), or null. */
export function authErrorCode(): string | null {
  return new URLSearchParams(window.location.search).get('auth_error');
}
